<template>
  <v-modal :modal-id="'productDetail'"
           :modal-class="'product-detail-modal'"
           :title-id="'productTitle'"
           :manual-focus="product.__typename === 'RegularProduct'"
           @close="close()">

    <product-info :product="product"
                  :configurations="configurations"
                  :customer-groups-to-show="customerGroupsToShow"
                  :terms="availableTerms"
                  :customer-group="customerGroup"
                  :date-effective="startDate"
                  @selectCustomerGroup="setCustomerGroup"/>

    <order-item-form
      :customer-groups-to-show="customerGroupsToShow"
      :data-loaded="filtered"
      :configuration-invalid.sync="configurationInvalid"
      :service-error.sync="serviceError"
    />

    <ul v-if="configurationInvalid || serviceError" class="ui-state-error" role="alert" aria-atomic="true"
        aria-live="polite">
      <li v-if="configurationInvalid">{{ $t('product-detail.form.validation.configuration-invalid') }}</li>
      <li v-if="serviceError">{{ $t('product-detail.form.validation.service-error') }}</li>
    </ul>
  </v-modal>
</template>

<script>
import { formatZoneMapDate, parseStoreDate } from '../../utils/date'
import { mapState, mapGetters } from 'vuex'
import OrderItemForm from '../../forms/OrderItem'
import ProductInfo from '../product-detail/Info'
import VModal from './Modal'
import { SET_SELECTED_CUSTOMER_GROUP } from '../../store/modules/configuration/mutation-types'

export default {
  components: {
    OrderItemForm,
    ProductInfo,
    VModal
  },
  data () {
    return {
      manuallyFocusedElement: null,
      expiryReminder: false,
      configurationInvalid: false,
      serviceError: false,
      filtered: false
    }
  },
  computed: {
    ...mapState({
      product: state => state.configuration.selectedProduct,
      term: state => state.configuration.selectedTerm,
      availableTerms: state => state.configuration.availableFilterEntities.terms,
      coveredPeriod: state => state.configuration.coveredPeriod,
      configurations: state => state.configuration.productConfigurations,
      fromStop: state => state.configuration.selectedFromStop,
      toStop: state => state.configuration.selectedToStop,
      dateEffective: state => state.configuration.dateEffective,
      starValue: state => state.configuration.selectedStarValue,
      zone: state => state.configuration.selectedZone,
      selectedClass: state => state.configuration.selectedClass,
      availableCustomerGroupsInConfiguration: state => state.configuration.availableFilterEntities.customerGroups
    }),
    ...mapGetters({
      availableSelectedCustomerGroups: 'selection/availableSelectedCustomerGroups',
      customerGroupToUseForConfiguration: 'configuration/customerGroupToUseForConfiguration',
      startDate: 'configuration/startDate',
      shopType: 'shop/type'
    }),
    customerGroup: function () {
      return this.customerGroupToUseForConfiguration
    },
    customerGroupSelectedInOverviewFilter: function () {
      return this.availableSelectedCustomerGroups.length > 0
        ? this.availableSelectedCustomerGroups[0]
        : null
    },
    filterProperties: function () {
      return [this.fromStop, this.toStop, this.selectedClass, this.dateEffective, this.starValue, this.zone]
    },
    customerGroupsToShow: function () {
      const customerGroups = this.availableCustomerGroupsInConfiguration.filter(
        customerGroup => customerGroup.nrOfProductConfigurations > 0
      )
      const filteredCustomerGroup = this.customerGroupSelectedInOverviewFilter
      if (filteredCustomerGroup) {
        return customerGroups.filter(customerGroup => customerGroup.id === filteredCustomerGroup.id)
      }

      return customerGroups.sort((a, b) => a.order - b.order)
    },
    regionDeeplinkId: function () {
      const region = this.$store.state.selection.selectedFilterEntities.regions.length > 0
        ? this.$store.state.selection.selectedFilterEntities.regions[0]
        : this.$store.state.selection.selectedFilterEntities.regions

      return region
        ? region.deeplinkId
        : null
    },
    agencyId: function () {
      if (this.shopType === 'NAL') {
        return this.$container.config.zoneMapAgencyId
      }

      if (this.shopType === 'ABT') {
        return this.$container.config.zoneMapAbtAgencyId
      }

      return null
    }
  },
  watch: {
    filterProperties: function () {
      this.filter()
    }
  },
  mounted: async function () {
    await this.getCoveredPeriod()
    await this.filter()
    this.filtered = true
    await this.applyPreSelection()
    this.$nextTick(function () {
      // Code that will run only after the entire view has been rendered
      const zoneMapElements = this.$el.querySelectorAll('a[href^="arriva://zone-map-for-current-selection"]')
      const openZoneMapToCurrentSelection = this.openZoneMapToCurrentSelection
      Array.prototype.forEach.call(zoneMapElements, zoneMapElement => {
        zoneMapElement.addEventListener('click', event => openZoneMapToCurrentSelection(event))
      })
    })
  },
  destroyed: function () {
    const zoneMapElements = this.$el.querySelectorAll('a[href^="arriva://zone-map-for-current-selection"]')
    const openZoneMapToCurrentSelection = this.openZoneMapToCurrentSelection

    Array.from(zoneMapElements).forEach(zoneMapElement => {
      zoneMapElement.removeEventListener('click', event => openZoneMapToCurrentSelection(event))
      zoneMapElement.addEventListener('click', event => openZoneMapToCurrentSelection(event))
    })
  },
  methods: {
    close: function () {
      this.$emit('close')
    },
    getCoveredPeriod: async function () {
      try {
        await this.$store.dispatch('configuration/fetchCoveredPeriod')
      } catch (error) {
        this.$log.debug(error)
        this.serviceError = true
      }
    },
    filter: async function () {
      try {
        await this.$store.dispatch('configuration/filter')
      } catch (error) {
        this.serviceError = true
      }
    },
    applyPreSelection: async function () {
      try {
        await this.$store.dispatch('configuration/applyPreselection')
      } catch (error) {
        this.serviceError = true
      }
    },
    setCustomerGroup: function (customerGroup) {
      this.$store.commit('configuration/' + SET_SELECTED_CUSTOMER_GROUP, customerGroup)
    },
    openZoneMapToCurrentSelection: function (e) {
      e.preventDefault()
      const params = {
        'productGroupID': this.product.deeplinkId,
        'agencyID': this.agencyId,
        'starValue': this.starValue ? this.starValue.deeplinkId : null,
        'engravedId': this.engravedId || null,
        'customerGroup': this.customerGroup ? this.customerGroup.deeplinkId : null,
        'tariffTerm': this.term ? this.term.deeplinkId : null,
        'dateEffective': this.dateEffective ? formatZoneMapDate(parseStoreDate(this.dateEffective)) : null,
        'regionId': this.regionDeeplinkId,
        'zoneCode': this.zone ? this.zone.code : null
      }

      const query = Object.keys(params)
        .filter(key => params[key])
        .map(key => {
          if (Array.isArray(params[key])) {
            return params[key]
              .map((value, i) => `${encodeURIComponent(key)}[${i}]=${encodeURIComponent(value)}`)
              .join('&')
          }

          return `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
        })
        .join('&')

      window.location.assign(`${this.$container.config.zoneMapUrl}?${query}`)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/modals/ProductDetail.vue